<template>
    <div class="deny-creative-form">
        <div class="form-group">
            <label>Reason</label>
            <select
                v-model="status_reason"
                class="form-control"
                :disabled="disabled"
            >
                <option value="">Select a reason...</option>
                <option
                    v-for="reason in reasons"
                    :key="reason"
                    :value="reason"
                >
                    {{ reason }}
                </option>
            </select>
            <div v-if="errors.status_reason" class="invalid-feedback d-block">
                {{ errors.status_reason }}
            </div>
        </div>

        <div class="form-group">
            <label>Memo</label>
            <textarea
                v-model="status_notes"
                class="form-control"
                rows="5"
                :disabled="disabled"
            ></textarea>
        </div>

        <div class="popper-footer d-flex justify-content-around">
            <button
                type="button"
                class="btn btn-cancel"
                @click="cancel"
                :disabled="submitting"
            >
                Cancel
            </button>
            <button
                type="button"
                class="btn btn-danger"
                @click="confirmSubmit"
                :disabled="disabled || submitting"
            >
                <span v-if="submitting">
                    <i class="fa fa-spinner fa-spin"></i> Submitting...
                </span>
                <span v-else>Submit</span>
            </button>
        </div>
    </div>
</template>

<script>
import CreativeTableApi from '@js/services/CreativeTableApi';

export default {
    name: 'DenyCreativeForm',

    props: {
        /**
         * Creative object with id, status_reason, and status_notes.
         */
        creative: {
            type: Object,
            required: true,
        },

        /**
         * Array of creative IDs to deny together.
         */
        creativeIds: {
            type: Array,
            default: () => [],
        },

        /**
         * Whether the form is disabled.
         */
        disabled: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            status_reason: this.creative.status_reason || '',
            status_notes: this.creative.status_notes || '',
            submitting: false,
            errors: {},
        };
    },

    computed: {
        /**
         * Get the list of deny reasons from window.vars.
         */
        reasons() {
            return window.vars?.denyAssetReasons || [];
        },
    },

    watch: {
        /**
         * Reset form when creative changes.
         */
        creative: {
            handler(newCreative) {
                this.status_reason = newCreative.status_reason || '';
                this.status_notes = newCreative.status_notes || '';
                this.errors = {};
            },
            deep: true,
        },
    },

    methods: {
        /**
         * Confirm before submitting denial.
         */
        confirmSubmit() {
            // Validate first
            this.errors = {};

            if (!this.status_reason) {
                this.errors.status_reason = 'The reason field is required.';
                return;
            }

            // Confirm with user
            if (confirm('Are you sure you want to deny this creative?')) {
                this.submit();
            }
        },

        /**
         * Submit the denial.
         */
        async submit() {
            this.submitting = true;

            try {
                await CreativeTableApi.deny(this.creative.id, {
                    status_reason: this.status_reason,
                    status_notes: this.status_notes,
                    creative_ids: this.creativeIds,
                });

                this.$emit('submitted');
                Bus.$emit('refreshReviewCreative');
                Bus.$emit('refreshOrdersTable');
                Bus.$emit('toast', 'Creative denied successfully.', 'success');
            } catch (error) {
                if (error.response?.data?.errors) {
                    this.errors = error.response.data.errors;
                } else {
                    Bus.$emit('toast', 'Failed to deny creative.', 'error');
                }
            } finally {
                this.submitting = false;
            }
        },

        /**
         * Cancel and close the form.
         */
        cancel() {
            this.$emit('cancel');
        },
    },
};
</script>

<style scoped>
.deny-creative-form {
    padding: 10px;
}

.form-group {
    margin-bottom: 1rem;
}

.form-group label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
}

.form-control {
    display: block;
    width: 100%;
    padding: 0.375rem 0.75rem;
    font-size: 1rem;
    line-height: 1.5;
    color: #495057;
    background-color: #fff;
    border: 1px solid #ced4da;
    border-radius: 0.25rem;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.form-control:focus {
    border-color: #80bdff;
    outline: 0;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.form-control:disabled {
    background-color: #e9ecef;
    opacity: 1;
}

.invalid-feedback {
    color: #dc3545;
    font-size: 0.875em;
    margin-top: 0.25rem;
}

.popper-footer {
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid #eee;
}

.btn {
    padding: 0.375rem 0.75rem;
    font-size: 1rem;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.15s ease-in-out;
}

.btn-cancel {
    background-color: #6c757d;
    color: #fff;
    border: 1px solid #6c757d;
}

.btn-cancel:hover:not(:disabled) {
    background-color: #5a6268;
    border-color: #545b62;
}

.btn-danger {
    background-color: #dc3545;
    color: #fff;
    border: 1px solid #dc3545;
}

.btn-danger:hover:not(:disabled) {
    background-color: #c82333;
    border-color: #bd2130;
}

.btn:disabled {
    opacity: 0.65;
    cursor: not-allowed;
}
</style>
